import { FC, useState, useCallback, useContext } from 'react';
import { FaUser, FaLock, FaExclamationTriangle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
// import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { AuthContext } from '@providers/Auth';
import { loginRequest } from '@providers/azureADAuthConfig';

import { signIn, TSignInParams } from '@api/requests';

import { setLoginProvider } from '@helpers/utils';
import { LoginProviders } from '@helpers/enums';

import Input from '@components/Input';
import Button from '@components/Button';
import Modal from '@components/Modal';

import LoaderImage from '@assets/loader.gif';
import MicrosoftIcon from '@assets/microsoft-icon.png';

import './LoginModal.scss';

const inputStyles = {
    marginBottom: 15,
};

const formDefaultValue = {
    userName: '',
    password: '',
    token: '',
    provider: LoginProviders.Identity,
    remember: false,
};

type LoginModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onForgotPasswordClick: () => void;
};

const LoginModal: FC<LoginModalProps> = ({
    isOpen,
    onClose,
    onForgotPasswordClick,
}) => {
    const isLoginAzureADEnabled = (process.env.REACT_APP_LOGIN_AZURE_AD_ENABLED && process.env.REACT_APP_LOGIN_AZURE_AD_ENABLED.toLowerCase() === "true") || false;
    const { instance } = useMsal();

    const [data, setData] = useState<TSignInParams>(formDefaultValue);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // const { executeRecaptcha } = useGoogleReCaptcha();

    const { setIsLoggedIn } = useContext(AuthContext);

    let navigate = useNavigate();

    const handleCloseLoginModal = useCallback(() => {
        !isLoading && onClose();
    }, [isLoading, onClose]);

    const LoginAzureADRedirect = useCallback(() => {
        instance.loginRedirect(loginRequest);
    }, [instance]);

    const handleSubmitLogin = useCallback(async () => {
        // async function verifyCaptcha() {
        //   if (!executeRecaptcha) {
        //     console.log('Execute recaptcha not yet available');
        //     return;
        //   }

        //   return await executeRecaptcha('verify');
        //   // Do whatever you want with the token
        // }

        // await verifyCaptcha();

        setIsLoading(true);
        setErrorMessage('');

        const [err] = await signIn(data);
        setIsLoading(false);

        if (err) {
            let msg = 'Something went wrong';

            switch (err) {
                case 'Request failed with status code 401':
                    msg = 'Please enter correct username and password';
                    break;

                default:
                    break;
            }
            setErrorMessage(msg);
        } else {
            setIsLoggedIn(true);
            navigate('/dashboard');
        }
    }, [data, navigate, setIsLoggedIn]);

    const handleLogin = useCallback((loginProvider: LoginProviders) => {
        setLoginProvider(loginProvider);
        switch (loginProvider) {
            case LoginProviders.Identity:
                handleSubmitLogin();
                break;

            case LoginProviders.AzureAD:
                LoginAzureADRedirect();
                break;
        }
    }, [handleSubmitLogin, LoginAzureADRedirect]);


    const handleChange = useCallback(e => {
        const { dataset, value, checked, type } = e.target;
        const { id } = dataset;
        const isCheckbox = type === 'checkbox';

        setData(prevState => ({
            ...prevState,
            [id]: isCheckbox ? checked : value,
        }));
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            handleLogin(LoginProviders.Identity);
        }
    }, [handleLogin]);

    return (
        <Modal
            title="LOGIN"
            opened={isOpen}
            invertedColors
            crossButton
            footer={false}
            onClose={handleCloseLoginModal}
            style={{ width: 600 }}
        >
            <div className="login-modal__form">
                <Input
                    data-id="userName"
                    placeholder="Username"
                    icon={<FaUser />}
                    style={inputStyles}
                    value={data.userName}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                />
                <Input
                    data-id="password"
                    placeholder="Password"
                    type="password"
                    icon={<FaLock />}
                    style={inputStyles}
                    value={data.password}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                />
                <div
                    className="login-modal__forgot-password"
                    onClick={onForgotPasswordClick}
                >
                    Forgot your password?
                </div>
                <div className="login-modal__checkbox-wrapper">
                    <label>
                        <input data-id="remember" type="checkbox" onChange={handleChange} />
                        <span>Remember me</span>
                    </label>
                </div>
                {errorMessage && (
                    <div className="login-modal__error-message">
                        <FaExclamationTriangle />
                        {errorMessage}
                    </div>
                )}
                <div className="mt-3 login-actions">
                    {isLoading ? 
                        <div className="login-actions__loading-image">
                            <img src={LoaderImage} alt="" />
                        </div>
                        :
                        <>
                            <Button label="Log&nbsp;In" disabled={isLoading} onClick={() => handleLogin(LoginProviders.Identity)} invertedColors />
                            {isLoginAzureADEnabled && <div className="or-container">
                                    <hr />
                                    <div className="or">OR</div>
                                    <hr />
                                </div>
                            }
                            {isLoginAzureADEnabled &&
                                <Button label="Sign in with Microsoft" disabled={isLoading} onClick={() => handleLogin(LoginProviders.AzureAD)} className="button_inverted-colors login-provider-button" image={MicrosoftIcon} />
                            }
                        </>
                    }
                </div>
                
            </div>
        </Modal>
    );
};

export default LoginModal;
